// Port of android/.../viewmodels/UserProfilePresentation.kt (the Apple profile has no local-time
// row yet; docs/PARITY.md "Other-user profile popover" names this file) plus the pure half of
// ios/Sources/Views/UserProfileView.swift (`statusLabel`) and UserProfileViewModel.
//
// Another person's LOCAL time (shared/API.md §2): `automaticTimezone` only when
// `useAutomaticTimezone == "true"`, otherwise `manualTimezone`. An empty or invalid selected zone
// OMITS the row — it is never replaced by the reader's own zone, which would state a wrong fact
// about where the other person is.
import { t } from '../i18n/i18n';

/** The wire's `User.timezone` (every field optional: sanitized users omit it). */
export interface UserTimezone {
  automaticTimezone?: string | null;
  manualTimezone?: string | null;
  /** A STRING boolean on the wire: "true" / "false". */
  useAutomaticTimezone?: string | boolean | null;
}

function isValidZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** The IANA zone the person selected, or null when there is none worth showing. */
export function selectedTimeZone(timezone: UserTimezone | null | undefined): string | null {
  if (!timezone) return null;
  const useAutomatic = timezone.useAutomaticTimezone === true || timezone.useAutomaticTimezone === 'true';
  const id = (useAutomatic ? timezone.automaticTimezone : timezone.manualTimezone) ?? '';
  if (id.trim() === '' || !isValidZone(id)) return null;
  return id;
}

/**
 * The person's wall-clock time now: "HH:mm" on a 24-hour clock, "h:mm a" in `locale` on a 12-hour
 * one. Null when the selected zone is missing or invalid (the row is omitted).
 */
export function localTime(args: {
  automaticTimezone: string | null;
  manualTimezone: string | null;
  useAutomaticTimezone: boolean;
  nowMillis: number;
  use24HourClock: boolean;
  locale: string;
}): string | null {
  const zone = selectedTimeZone({
    automaticTimezone: args.automaticTimezone,
    manualTimezone: args.manualTimezone,
    useAutomaticTimezone: args.useAutomaticTimezone,
  });
  if (!zone) return null;
  const date = new Date(args.nowMillis);
  if (args.use24HourClock) {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(date);
    const hour = parts.find((p) => p.type === 'hour')?.value ?? '00';
    const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
    return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  }
  // ICU separates the day period with a NARROW no-break space; java.time's "h:mm a" uses a plain
  // space. Normalize so both clients write the same string.
  return new Intl.DateTimeFormat(args.locale, { timeZone: zone, hour: 'numeric', minute: '2-digit', hour12: true })
    .format(date)
    .replace(/[  ]/g, ' ');
}

/** UserProfileView.statusLabel: the presence value as a sentence. Out of office is server-set. */
export function statusLabel(status: string): string {
  switch (status) {
    case 'online':
      return t('profile.status.online');
    case 'away':
      return t('profile.status.away');
    case 'dnd':
      return t('profile.status.dnd');
    // Out of office follows the auto-reply, so it only ever arrives here, never from a picker.
    // Falling through to "Desconectado" told the reader the person was offline when the person
    // had said exactly where they were.
    case 'ooo':
      return t('profile.status.ooo');
    default:
      return t('profile.status.offline');
  }
}

export const UserProfilePresentation = { selectedTimeZone, localTime, statusLabel } as const;
